import { FC, useEffect, useRef, useState } from 'react';

import { Link } from '@inertiajs/react';
import { MessageSquareText } from 'lucide-react';

import {
    Avatar,
    AvatarFallback,
    AvatarImage,
} from '../../components/ui/Avatar';
import { Badge } from '../../components/ui/Badge';
import { Card, CardContent } from '../../components/ui/Card';
import {
    getOrderReviewComplaintLabel,
    getOrderReviewRatingLabel,
} from '../../models/orderReview';
import { OrderReview } from '../../types';
import { cn, formatDate } from '../../utils';

const ratingBadgeVariant = (rating: OrderReview['overall_rating']) => {
    switch (rating) {
        case 'very_good':
            return 'success';
        case 'good':
            return 'info';
        case 'neutral':
            return 'neutral';
        case 'bad':
            return 'warning';
        case 'very_bad':
            return 'destructive';
        default:
            return 'outline';
    }
};

export const SellerReviewCard: FC<{ review: OrderReview }> = ({ review }) => {
    const [expanded, setExpanded] = useState(false);
    const [canExpand, setCanExpand] = useState(false);
    const commentRef = useRef<HTMLParagraphElement | null>(null);
    const buyerInitials =
        review.buyer?.nickname
            ?.split(' ')
            .map((value) => value[0])
            .join('')
            .slice(0, 2) || 'U';
    const comment = review.comment?.trim() || '';
    const hasPackagingRating = Boolean(review.packaging_rating);
    const complaintLabels = review.complaints
        .map(getOrderReviewComplaintLabel)
        .filter((label): label is string => Boolean(label));

    useEffect(() => {
        if (!comment || expanded) return;

        const measureExpandable = () => {
            const element = commentRef.current;
            if (!element) return;

            setCanExpand(element.scrollHeight > element.clientHeight + 1);
        };

        const frameId = window.requestAnimationFrame(measureExpandable);

        window.addEventListener('resize', measureExpandable);

        return () => {
            window.cancelAnimationFrame(frameId);
            window.removeEventListener('resize', measureExpandable);
        };
    }, [comment, expanded]);

    return (
        <Card className="h-full min-w-0 rounded-2xl">
            <CardContent className="flex h-full flex-col space-y-4 p-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex min-w-0 items-center gap-3">
                        <Avatar className="h-11 w-11">
                            <AvatarImage
                                src={review.buyer?.avatar}
                                alt={review.buyer?.nickname || 'Покупець'}
                            />
                            <AvatarFallback>{buyerInitials}</AvatarFallback>
                        </Avatar>

                        <div className="min-w-0 space-y-0.5">
                            {review.buyer ? (
                                <Link
                                    href={review.buyer.url_path}
                                    className="block font-semibold break-words hover:underline"
                                >
                                    {review.buyer.nickname}
                                </Link>
                            ) : (
                                <p className="font-semibold">Покупець</p>
                            )}
                            <p className="text-sm text-muted-foreground">
                                {formatDate(review.created_at)}
                            </p>
                        </div>
                    </div>

                    <Badge
                        variant={ratingBadgeVariant(review.overall_rating)}
                        className="max-w-full self-start text-center break-words whitespace-normal"
                    >
                        {getOrderReviewRatingLabel(review.overall_rating)}
                    </Badge>
                </div>

                <div className="flex flex-wrap gap-2">
                    <Badge
                        variant="outline"
                        className="max-w-full break-words whitespace-normal"
                    >
                        Загалом:{' '}
                        {getOrderReviewRatingLabel(review.overall_rating)}
                    </Badge>
                    <Badge
                        variant="outline"
                        className="max-w-full break-words whitespace-normal"
                    >
                        Опис:{' '}
                        {getOrderReviewRatingLabel(
                            review.item_description_rating,
                        )}
                    </Badge>
                    {hasPackagingRating && (
                        <Badge
                            variant="outline"
                            className="max-w-full break-words whitespace-normal"
                        >
                            Пакування:{' '}
                            {getOrderReviewRatingLabel(review.packaging_rating)}
                        </Badge>
                    )}
                </div>

                <div className="rounded-lg bg-slate-50 p-4 text-sm leading-6 dark:bg-slate-800/80 dark:text-slate-100">
                    {comment ? (
                        <div className="space-y-3">
                            <p
                                ref={commentRef}
                                className={cn(
                                    'break-words whitespace-pre-wrap',
                                    !expanded && 'line-clamp-5',
                                )}
                            >
                                {comment}
                            </p>

                            {canExpand && (
                                <button
                                    type="button"
                                    onClick={() =>
                                        setExpanded((current) => !current)
                                    }
                                    className="inline-flex cursor-pointer text-sm font-medium text-primary transition-colors hover:underline"
                                >
                                    {expanded ? 'Згорнути' : 'Показати більше'}
                                </button>
                            )}
                        </div>
                    ) : (
                        <span className="text-muted-foreground">
                            Покупець залишив оцінку без текстового коментаря.
                        </span>
                    )}
                </div>

                {complaintLabels.length > 0 && (
                    <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm font-medium">
                            <MessageSquareText className="h-4 w-4 text-muted-foreground" />
                            Скарги
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {complaintLabels.map((label) => (
                                <Badge
                                    key={label}
                                    variant="neutral"
                                    className="max-w-full break-words whitespace-normal"
                                >
                                    {label}
                                </Badge>
                            ))}
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};
